import { createContext, useContext, useState, useEffect } from 'react';
import { authApi } from '../api/client';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);
  const [error, setError] = useState(null);
  const [authConfig, setAuthConfig] = useState(null);

  // Fetch Auth0 configuration from backend on mount
  useEffect(() => {
    fetchAuthConfig();
  }, []);

  const fetchAuthConfig = async () => {
    try {
      console.log('🔧 Fetching auth config from:', import.meta.env.VITE_API_BASE_URL ? `${import.meta.env.VITE_API_BASE_URL}/config/auth` : '/config/auth (proxy)');
      const config = await authApi.getConfig();
      console.log('✅ Auth config loaded:', { domain: config.auth0_domain, client_id: config.client_id });
      setAuthConfig(config);
    } catch (err) {
      console.error('❌ Config fetch failed:', err);
      console.log('⚠️ Using hardcoded auth config as fallback');
      // Fallback: use hardcoded production config if API times out
      // This prevents cold starts from breaking auth entirely
      const fallbackConfig = {
        auth0_domain: 'dev-md6ue3r5htjtge4u.us.auth0.com',
        client_id: '3W9epSAcRK721EO8LATZMLigV9WcKXlo',
        redirect_uri: `${window.location.origin}/callback`,
        logout_redirect_uri: window.location.origin
      };
      setAuthConfig(fallbackConfig);
      setError('Using fallback auth configuration (API timeout)');
    }
  };

  // Check authentication status after config is loaded
  // Skip on /callback and /access-denied routes to avoid race conditions and loops
  useEffect(() => {
    const pathname = window.location.pathname;
    const skipAuthCheck = pathname === '/callback' || pathname === '/access-denied';
    
    if (authConfig && !skipAuthCheck) {
      checkAuth();
    } else if (skipAuthCheck) {
      // On callback or access-denied pages, don't check auth
      setLoading(false);
    }
  }, [authConfig]);

  const checkAuth = async () => {
    try {
      const { response, data } = await authApi.getMe();
      
      if (response.ok) {
        console.log('🔍 Auth /auth/me response:', data);
        setUser(data.user);
        setLoading(false);
      } else if (response.status === 401) {
        // Not authenticated - redirect to Auth0 login
        console.log('🚪 User not authenticated, redirecting to login');
        redirectToLogin();
        return; // Don't set loading=false - we're redirecting away
      } else {
        // Other error status - also redirect to login
        console.log(`⚠️ Auth check returned ${response.status}, redirecting to login`);
        redirectToLogin();
        return;
      }
    } catch (err) {
      console.error('❌ Auth check failed (network/API error):', err);
      console.log('⚠️ Redirecting to login due to auth check failure');
      // Network error or API unreachable - redirect to login instead of showing landing page
      // This prevents unauthorized access when API is down or misconfigured
      redirectToLogin();
      return;
    }
  };

  const redirectToLogin = () => {
    if (!authConfig) {
      console.error('Auth config not loaded');
      return;
    }

    // Keep loading state true during redirect to prevent flash
    // Don't set loading to false - we're redirecting away
    
    const redirectUri = encodeURIComponent(authConfig.redirect_uri);
    const responseType = 'code';
    const scope = encodeURIComponent('openid profile email');
    
    const authUrl = `https://${authConfig.auth0_domain}/authorize?` +
      `response_type=${responseType}&` +
      `client_id=${authConfig.client_id}&` +
      `redirect_uri=${redirectUri}&` +
      `scope=${scope}`;
    
    window.location.href = authUrl;
  };

  const logout = async () => {
    if (!authConfig) {
      console.error('Auth config not loaded');
      return;
    }

    // Set logging out state immediately to show logout screen
    setLoggingOut(true);
    setUser(null);

    // Fire and forget - backend cleanup happens in background
    authApi.logout().catch(err => console.error('Logout cleanup failed:', err));
    
    // Redirect to Auth0 logout
    const returnTo = encodeURIComponent(authConfig.logout_redirect_uri || window.location.origin);
    window.location.href = `https://${authConfig.auth0_domain}/v2/logout?returnTo=${returnTo}`;
    
    // Block function return to prevent React re-render before browser navigates
    // This keeps the logout screen visible until the redirect completes
    await new Promise(() => {}); // Never resolves - hangs until page unloads
  };

  return (
    <AuthContext.Provider value={{ user, loading, loggingOut, error, logout, checkAuth, authConfig }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
